import React, { useState, useCallback, useEffect } from 'react';
import { useLoadScript, GoogleMap, Autocomplete } from '@react-google-maps/api';
import { MapPin, Link } from 'lucide-react';
import { GOOGLE_MAPS_API_KEY } from '../../config/keys';

const LIMA_COORDS = { lat: -12.0464, lng: -77.0428 };
// Define libraries como constante fuera del componente para evitar recargas innecesarias
const LIBRARIES = ['places'];

const formatGoogleAddress = (addressComponents) => {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return '';
  }
  
  const getComponent = (type) => {
    const component = addressComponents.find(comp => comp.types.includes(type));
    return component ? component.long_name : '';
  };

  const city = getComponent('locality') || getComponent('administrative_area_level_2');
  const region = getComponent('administrative_area_level_1');
  const country = getComponent('country');

  const parts = [city, region, country].filter(Boolean);
  return parts.join(', ');
};

// Función simplificada y más robusta para manejar enlaces de Google Maps
const processGoogleMapsLink = async (link, map) => {
  if (!link || !map) return null;
  
  try {
    // Estrategia 1: Extraer coordenadas directamente del enlace (más confiable)
    const patterns = [
      // Patrón de URL con @lat,lng,zoom
      /@(-?\d+\.\d+),(-?\d+\.\d+)/,
      // Patrón de URL con ll=lat,lng
      /ll=(-?\d+\.\d+),(-?\d+\.\d+)/,
      // Patrón de URL con q=lat,lng
      /q=(-?\d+\.\d+),(-?\d+\.\d+)/,
      // Patrón para extraer coordenadas de los parámetros de la URL
      /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
      // Patrón adicional
      /3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
      // Patrón para coordenadas en formato center
      /center=(-?\d+\.\d+),(-?\d+\.\d+)/,
      // Patrón para coordenadas después de @
      /@(-?\d+\.\d+),(-?\d+\.\d+),\d+/
    ];

    // Intentar expandir enlaces acortados primero
    let processedLink = link;
    if (link.includes('goo.gl') || link.includes('maps.app.goo.gl')) {
      try {
        // Crear un iframe invisible para obtener la URL expandida
        const expandedUrl = await expandUrlWithIframe(link);
        if (expandedUrl && expandedUrl !== link) {
          processedLink = expandedUrl;
        }
      } catch (error) {
        console.log('No se pudo expandir automáticamente, procesando enlace original...');
      }
    }

    // Buscar coordenadas en el enlace (original o expandido)
    for (const pattern of patterns) {
      const match = processedLink.match(pattern);
      if (match && match[1] && match[2]) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        
        // Validar que las coordenadas sean válidas
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return {
            lat: lat,
            lng: lng
          };
        }
      }
    }

    // Estrategia 2: Extraer place_id y usar Geocoding API como alternativa
    const placeIdMatch = processedLink.match(/place_id[=:]([A-Za-z0-9_-]+)/);
    if (placeIdMatch && window.google && window.google.maps && window.google.maps.Geocoder) {
      return new Promise((resolve) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ 
          placeId: placeIdMatch[1] 
        }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
              address: results[0].formatted_address,
              addressComponents: results[0].address_components
            });
          } else {
            resolve(null);
          }
        });
      });
    }

    // Estrategia 3: Extraer nombre del lugar y usar Geocoding API
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
      return new Promise((resolve) => {
        const geocoder = new window.google.maps.Geocoder();
        
        // Extraer posible texto de dirección del enlace
        let searchText = processedLink;
        const placeMatch = processedLink.match(/\/place\/([^\/\?#]+)/);
        if (placeMatch) {
          searchText = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
        }
        
        // Agregar ", Perú" para mejorar la búsqueda
        if (!searchText.toLowerCase().includes('peru') && !searchText.toLowerCase().includes('perú')) {
          searchText += ', Perú';
        }
        
        geocoder.geocode({ 
          address: searchText,
          componentRestrictions: { country: 'PE' }
        }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
              address: results[0].formatted_address,
              addressComponents: results[0].address_components
            });
          } else {
            resolve(null);
          }
        });
      });
    }

    return null;
  } catch (error) {
    console.error('Error al procesar el enlace de Google Maps:', error);
    return null;
  }
};

// Función mejorada para expandir URL usando iframe (método seguro)
const expandUrlWithIframe = (shortUrl) => {
  return new Promise((resolve) => {
    let resolved = false;
    
    // Crear iframe invisible
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    
    // Timeout de seguridad
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(null);
      }
    }, 5000); // 5 segundos de timeout
    
    const cleanup = () => {
      clearTimeout(timeout);
      try {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      } catch (e) {
        // Ignorar errores de limpieza
      }
    };
    
    iframe.onload = () => {
      if (resolved) return;
      
      try {
        // Intentar obtener la URL del iframe
        const currentUrl = iframe.contentWindow.location.href;
        if (currentUrl && currentUrl !== shortUrl && currentUrl !== 'about:blank') {
          resolved = true;
          cleanup();
          resolve(currentUrl);
          return;
        }
      } catch (error) {
        // Error de CORS, esto es normal para enlaces externos
      }
      
      // Si no podemos acceder a contentWindow, intentar otros métodos
      setTimeout(() => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve(null);
      }, 2000);
    };
    
    iframe.onerror = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(null);
    };
    
    // Intentar cargar el enlace
    try {
      iframe.src = shortUrl;
      document.body.appendChild(iframe);
    } catch (error) {
      resolved = true;
      cleanup();
      resolve(null);
    }
  });
};

const SedeLocation = ({ 
  location = {}, 
  onLocationSelect 
}) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [address, setAddress] = useState(location.direccion_sede || '');
  const [loadError, setLoadError] = useState(null);
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [autocomplete, setAutocomplete] = useState(null);
  const [isProcessingLink, setIsProcessingLink] = useState(false);
  const [center, setCenter] = useState(
    location.lat_sede && location.lng_sede
      ? { 
          lat: parseFloat(location.lat_sede), 
          lng: parseFloat(location.lng_sede) 
        } 
      : LIMA_COORDS
  );

  // Usar la geolocalización del navegador para centrar el mapa
  useEffect(() => {
    if (!location.lat_sede && !location.lng_sede && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCenter(userLocation);
          
          if (map) {
            map.panTo(userLocation);
          }
        },
        (error) => {
          console.error("Error obteniendo la ubicación del usuario:", error);
        }
      );
    }
  }, [map, location.lat_sede, location.lng_sede]);

  const { isLoaded, loadError: googleLoadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  });

  const updateLocation = useCallback(async (latLng, addressInfo = null) => {
    if (!map) return;

    // Eliminar marcador anterior si existe
    if (marker) {
      marker.setMap(null);
    }

    const newCenter = { 
      lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat, 
      lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng 
    };
    
    setCenter(newCenter);
    map.panTo(newCenter);

    const newMarker = new window.google.maps.Marker({
      position: newCenter,
      map: map,
      title: 'Ubicación de la Sede'
    });
    setMarker(newMarker);

    // Si no hay información de dirección, intentar obtenerla con geocodificación inversa
    let fullAddress = addressInfo?.formatted_address || address;
    let simplifiedAddress = '';
    
    if (!addressInfo && isLoaded) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({ 
          location: newCenter 
        });
        
        if (response.results[0]) {
          fullAddress = response.results[0].formatted_address;
          simplifiedAddress = formatGoogleAddress(response.results[0].address_components);
        }
      } catch (error) {
        console.error('Error en geocodificación inversa:', error);
      }
    } else if (addressInfo?.address_components) {
      simplifiedAddress = formatGoogleAddress(addressInfo.address_components);
    }

    // Llamar al callback con ambas direcciones
    const locationData = {
      formatted_address: fullAddress,
      simplified_address: simplifiedAddress,
      lat: newCenter.lat,
      lng: newCenter.lng
    };
    
    onLocationSelect(locationData);

    // Actualizar el input con la dirección completa
    if (fullAddress) {
      setAddress(fullAddress);
    }
  }, [map, marker, onLocationSelect, address, isLoaded]);

  const handlePlaceSelect = useCallback(() => {
    if (!autocomplete) return;
    
    try {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        updateLocation(place.geometry.location, place);
      }
    } catch (error) {
      console.error('Error selecting place:', error);
      setLoadError('Error al seleccionar la ubicación');
    }
  }, [autocomplete, updateLocation]);

  const handleMapClick = useCallback(async (event) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ 
        location: { 
          lat: event.latLng.lat(), 
          lng: event.latLng.lng() 
        } 
      });
      
      if (response.results[0]) {
        updateLocation(event.latLng, response.results[0]);
      } else {
        updateLocation(event.latLng);
      }
    } catch (error) {
      console.error('Error geocoding:', error);
      setLoadError('Error al obtener la dirección');
    }
  }, [updateLocation]);

  // Función mejorada para manejar enlaces de Google Maps
  const handleGoogleMapsLink = useCallback(async () => {
    if (!googleMapsLink || !isLoaded || !map) return;
    
    setIsProcessingLink(true);
    setLoadError('Procesando enlace...');
    
    try {
      // Usar la función mejorada para procesar el enlace
      const result = await processGoogleMapsLink(googleMapsLink, map);
      
      if (result) {
        // Crear objeto de coordenadas
        const coordsObj = {
          lat: result.lat,
          lng: result.lng
        };
        
        // Si tenemos información de dirección del resultado, usarla
        if (result.address) {
          const addressInfo = {
            formatted_address: result.address,
            address_components: result.addressComponents
          };
          await updateLocation(coordsObj, addressInfo);
        } else {
          // Si no, usar geocodificación inversa
          await updateLocation(coordsObj);
        }
        
        // Limpiar el input del enlace después de procesarlo exitosamente
        setGoogleMapsLink('');
        setLoadError(null);
      } else {
        setLoadError('No se pudo encontrar la ubicación. Verifica que el enlace sea válido de Google Maps.');
      }
    } catch (error) {
      console.error('Error al procesar el enlace de Google Maps:', error);
      setLoadError('Error al procesar el enlace. Intenta con un enlace diferente o busca la dirección manualmente.');
    } finally {
      setIsProcessingLink(false);
    }
  }, [googleMapsLink, isLoaded, map, updateLocation]);

  if (googleLoadError) {
    return (
      <div className="p-4 text-red-500 border border-red-300 rounded-lg">
        Error al cargar el mapa. Por favor, recarga la página o contacta al soporte.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">
          Dirección de la Sede*
        </label>
        <Autocomplete
          onLoad={setAutocomplete}
          onPlaceChanged={handlePlaceSelect}
          options={{
            componentRestrictions: { country: 'pe' },
            types: ['address'],
            fields: ['address_components', 'formatted_address', 'geometry']
          }}
        >
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Buscar dirección de la sede"
          />
        </Autocomplete>
      </div>

      {/* Campo mejorado para el enlace de Google Maps */}
      <div>
        <label className="block text-sm font-medium mb-1 flex items-center gap-1">
          <Link className="h-4 w-4" />
          Enlace de Google Maps (opcional)
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={googleMapsLink}
            onChange={(e) => setGoogleMapsLink(e.target.value)}
            className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Pega cualquier enlace de Google Maps aquí"
            disabled={isProcessingLink}
          />
          <button
            onClick={handleGoogleMapsLink}
            className={`px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[80px] ${
              isProcessingLink 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
            disabled={!googleMapsLink || isProcessingLink}
          >
            {isProcessingLink ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto"></div>
            ) : (
              'Buscar'
            )}
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-1 space-y-1">
          <p>✅ Enlaces completos: https://www.google.com/maps/place/...</p>
          <p>✅ Enlaces acortados: https://maps.app.goo.gl/...</p>
          <p>✅ Enlaces compartidos desde la app móvil de Google Maps</p>
          <p>💡 <strong>Tip:</strong> Copia el enlace desde "Compartir" en Google Maps</p>
        </div>
      </div>

      {loadError && (
        <div className={`text-sm p-3 rounded-lg ${
          loadError.includes('Procesando') 
            ? 'text-blue-600 bg-blue-50 border border-blue-200' 
            : 'text-red-600 bg-red-50 border border-red-200'
        }`}>
          {loadError}
        </div>
      )}

      <div className="h-96 rounded-lg overflow-hidden border-2 border-gray-200">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={15}
          options={{
            zoomControl: true,
            fullscreenControl: true,
            streetViewControl: false,
            mapTypeControl: false,
          }}
          onLoad={setMap}
          onClick={handleMapClick}
        />
      </div>

      <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
          <div>
            <p className="font-medium text-gray-700 mb-2">¿Cómo obtener un enlace de Google Maps?</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Abre Google Maps en tu teléfono o computadora</li>
              <li>Busca y selecciona tu ubicación</li>
              <li>Toca "Compartir" y copia el enlace</li>
              <li>Pega el enlace arriba y presiona "Buscar"</li>
            </ol>
            <p className="mt-3 text-xs">
              <strong>Alternativa:</strong> También puedes buscar la dirección o hacer clic directamente en el mapa.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SedeLocation;